// src/components/AiGesprekkenLijst.js
//
// Gesprekkenlijst naast de AI-assistent op het dashboard: kiezen, nieuw gesprek,
// hernoemen, downloaden en verwijderen. De titels zijn de woorden van de leraar,
// dus de lijst draagt data-eigen-gegevens en gaat niet mee als paginatekst (aiContext.js).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import {
  AI_GESPREKKEN_CHANGED,
  deleteAiGesprek,
  downloadGesprekPdf,
  fetchTranscript,
  listAiGesprekken,
  registerAiGesprek,
  renameAiGesprek,
  titelUitTranscript,
} from '../utils/aiGesprekken';
import './AiGesprekkenLijst.css';

const COLLAPSED_KEY = 'ai_gesprekken_ingeklapt';

// Iconen in dezelfde lijnstijl als de rest van de site (feather).
const Icoon = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const IconHernoemen = () => (
  <Icoon>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
  </Icoon>
);

const IconDownload = () => (
  <Icoon>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </Icoon>
);

const IconNieuwGesprek = () => (
  <Icoon>
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    <line x1="12" y1="7" x2="12" y2="13" />
    <line x1="9" y1="10" x2="15" y2="10" />
  </Icoon>
);

const IconVerwijderen = () => (
  <Icoon>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
  </Icoon>
);

function datumLabel(value) {
  if (!value) return '';
  const d = new Date(value);
  const vandaag = new Date();
  const gisteren = new Date();
  gisteren.setDate(vandaag.getDate() - 1);
  if (d.toDateString() === vandaag.toDateString()) {
    return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  }
  if (d.toDateString() === gisteren.toDateString()) return 'Gisteren';
  return d.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== vandaag.getFullYear() ? { year: 'numeric' } : {}),
  });
}

const MOBIEL_ZICHTBAAR = 4;

// Op mobiel staat de chat niet naast de lijst maar opent hij schermvullend, net als
// via de knop AI in het ondermenu.
function openAssistent() {
  if (window.PAP_Chat && window.PAP_Chat.open) {
    window.PAP_Chat.open({ state: 'maximized' });
  } else {
    window.dispatchEvent(new CustomEvent('open-ai-assistant'));
  }
}

// variant 'naast-chat': zijbalk naast de ingebedde chat (desktop).
// variant 'kaart': kaart op het mobiele dashboard; kiezen opent de chat.
export default function AiGesprekkenLijst({ variant = 'naast-chat', alles = false }) {
  const isKaart = variant === 'kaart';
  const { currentUser, globalSettings, switchAiSession } = useDoorloopjes();
  const [alleTonen, setAlleTonen] = useState(false);
  const activeId = currentUser?.ai_session_id || null;

  const [gesprekken, setGesprekken] = useState([]);
  const [loading, setLoading] = useState(true);
  const [melding, setMelding] = useState(null);
  const [bewerkId, setBewerkId] = useState(null);
  const [bewerkTitel, setBewerkTitel] = useState('');
  const [verwijderId, setVerwijderId] = useState(null);
  const [bezigId, setBezigId] = useState(null);
  const [ingeklapt, setIngeklapt] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const titelsBezig = useRef(new Set());
  const titelPogingen = useRef(new Map());
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const gesprekkenRef = useRef(gesprekken);
  gesprekkenRef.current = gesprekken;

  // Gesprekken zonder titel krijgen de eerste vraag als titel. Het gesprek wordt
  // aangemeld zodra de vraag verstuurd is, maar staat pas in PAP als het antwoord
  // loopt. Een nieuw gesprek proberen we daarom een paar keer, met een pauze.
  const vulTitels = useCallback(async (lijst) => {
    const zonderTitel = lijst.filter((g) => !g.titel && !titelsBezig.current.has(g.documentId)).slice(0, 5);
    for (const g of zonderTitel) {
      const leeftijd = Date.now() - new Date(g.createdAt).getTime();
      const poging = titelPogingen.current.get(g.documentId) || 0;
      const opnieuw = () => {
        if (leeftijd > 5 * 60_000 || poging >= 4) return;
        titelPogingen.current.set(g.documentId, poging + 1);
        setTimeout(() => vulTitels([g]), 8000);
      };
      if (leeftijd < 5000 && poging === 0) {
        opnieuw();
        continue;
      }

      titelsBezig.current.add(g.documentId);
      try {
        const messages = await fetchTranscript(g.pap_conversation_id);
        const titel = titelUitTranscript(messages);
        // Intussen zelf hernoemd of verwijderd: niet overschrijven.
        const huidig = gesprekkenRef.current.find((x) => x.documentId === g.documentId);
        if (!huidig || huidig.titel) continue;
        if (titel) {
          const updated = await renameAiGesprek(g.documentId, titel);
          if (updated) {
            setGesprekken((prev) => prev.map((x) => (x.documentId === g.documentId ? { ...x, titel: updated.titel } : x)));
          }
        } else {
          opnieuw();
        }
      } catch (error) {
        console.error('Titel bepalen mislukt:', error);
      } finally {
        titelsBezig.current.delete(g.documentId);
      }
    }
  }, []);

  const laden = useCallback(async () => {
    try {
      let lijst = await listAiGesprekken();
      // Het gesprek van voor de gesprekkenlijst staat alleen als actief id bij de
      // gebruiker. Eenmalig toevoegen, maar alleen als PAP het nog heeft: in
      // september 2026 was dat bij ongeveer een kwart zo; de rest is na 90 dagen
      // opgeschoond en zou als leeg gesprek in de lijst komen.
      const actief = activeIdRef.current;
      if (actief && !lijst.some((g) => g.pap_conversation_id === actief)) {
        const messages = await fetchTranscript(actief).catch(() => null);
        if (messages && messages.some((m) => m.sender === 'user')) {
          const nieuw = await registerAiGesprek(actief);
          if (nieuw) lijst = [nieuw, ...lijst];
        }
      }
      setGesprekken(lijst);
      vulTitels(lijst);
    } catch (error) {
      console.error('Gesprekken laden mislukt:', error);
      setMelding('Je gesprekken laden lukte niet.');
    } finally {
      setLoading(false);
    }
  }, [vulTitels]);

  useEffect(() => {
    laden();
    window.addEventListener(AI_GESPREKKEN_CHANGED, laden);
    return () => window.removeEventListener(AI_GESPREKKEN_CHANGED, laden);
  }, [laden]);

  const toggleIngeklapt = () => {
    setIngeklapt((prev) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, prev ? '0' : '1');
      } catch {
        // alleen een voorkeur
      }
      return !prev;
    });
  };

  const kies = (g) => {
    if (bewerkId) return;
    setMelding(null);
    if (g.pap_conversation_id !== activeId) switchAiSession(g.pap_conversation_id);
    if (isKaart) openAssistent();
  };

  const nieuwGesprek = () => {
    setMelding(null);
    switchAiSession(null);
    if (isKaart) openAssistent();
  };

  const startHernoemen = (g) => {
    setBewerkId(g.documentId);
    setBewerkTitel(g.titel || '');
  };

  const opslaanTitel = async () => {
    const id = bewerkId;
    const titel = bewerkTitel.trim();
    setBewerkId(null);
    if (!id) return;
    const vorige = gesprekken.find((g) => g.documentId === id);
    if (!titel || titel === vorige?.titel) return;
    setGesprekken((prev) => prev.map((g) => (g.documentId === id ? { ...g, titel } : g)));
    try {
      await renameAiGesprek(id, titel);
    } catch (error) {
      console.error('Hernoemen mislukt:', error);
      setGesprekken((prev) => prev.map((g) => (g.documentId === id ? { ...g, titel: vorige?.titel } : g)));
      setMelding('Hernoemen lukte niet.');
    }
  };

  const download = async (g) => {
    setBezigId(g.documentId);
    setMelding(null);
    try {
      const gelukt = await downloadGesprekPdf(g, { logoUrl: globalSettings?.logo?.url });
      if (!gelukt) setMelding('Dit gesprek is niet meer beschikbaar.');
    } catch (error) {
      console.error('Downloaden mislukt:', error);
      setMelding('Downloaden lukte niet.');
    } finally {
      setBezigId(null);
    }
  };

  const verwijder = async (g) => {
    setVerwijderId(null);
    setBezigId(g.documentId);
    setMelding(null);
    try {
      await deleteAiGesprek(g.documentId);
      setGesprekken((prev) => prev.filter((x) => x.documentId !== g.documentId));
      if (g.pap_conversation_id === activeId) switchAiSession(null);
    } catch (error) {
      console.error('Verwijderen mislukt:', error);
      setMelding('Verwijderen lukte niet.');
    } finally {
      setBezigId(null);
    }
  };

  if (ingeklapt && !isKaart) {
    return (
      <nav className="ai-gesprekken ai-gesprekken--ingeklapt" aria-label="Gesprekken" data-eigen-gegevens>
        <button
          type="button"
          className="ai-gesprekken__icoonknop"
          onClick={toggleIngeklapt}
          title="Gesprekken tonen"
          aria-label="Gesprekken tonen"
        >
          ☰
        </button>
        <button
          type="button"
          className="ai-gesprekken__icoonknop"
          onClick={nieuwGesprek}
          title="Nieuw gesprek"
          aria-label="Nieuw gesprek"
        >
          <IconNieuwGesprek />
        </button>
      </nav>
    );
  }

  const inkorten = isKaart && !alles && !alleTonen;
  const zichtbaar = inkorten ? gesprekken.slice(0, MOBIEL_ZICHTBAAR) : gesprekken;
  // Na "Nieuw gesprek" is er nog geen gesprek in PAP (dat ontstaat bij de eerste vraag).
  // Toon het lege gesprek toch bovenaan als actief, anders lijkt de knop niets te doen.
  const toonNieuw = !loading && !activeId && gesprekken.length > 0;

  return (
    <nav
      className={isKaart ? 'widget-card ai-gesprekken ai-gesprekken--kaart' : 'ai-gesprekken'}
      aria-label="Gesprekken"
      data-eigen-gegevens
    >
      <div className="ai-gesprekken__kop">
        <h3>{isKaart ? 'Gesprekken met Doortje' : 'Gesprekken'}</h3>
        <div className="ai-gesprekken__kopknoppen">
          <button
            type="button"
            className="ai-gesprekken__icoonknop"
            onClick={nieuwGesprek}
            title="Nieuw gesprek"
            aria-label="Nieuw gesprek"
          >
            <IconNieuwGesprek />
          </button>
          {!isKaart && (
            <button
              type="button"
              className="ai-gesprekken__icoonknop"
              onClick={toggleIngeklapt}
              title="Gesprekken verbergen"
              aria-label="Gesprekken verbergen"
            >
              «
            </button>
          )}
        </div>
      </div>

      {melding && (
        <p className="ai-gesprekken__melding" role="status">
          {melding}
        </p>
      )}

      <ul className="ai-gesprekken__lijst">
        {loading && <li className="ai-gesprekken__leeg">Laden…</li>}
        {!loading && gesprekken.length === 0 && (
          <li className="ai-gesprekken__leeg">Nog geen gesprekken. Stel Doortje een vraag om te beginnen.</li>
        )}
        {toonNieuw && (
          <li className="ai-gesprekken__item ai-gesprekken__item--actief">
            <button
              type="button"
              className="ai-gesprekken__kies"
              onClick={isKaart ? openAssistent : undefined}
              aria-current="true"
              title="Nieuw gesprek"
            >
              <span className="ai-gesprekken__titel">Nieuw gesprek</span>
              <span className="ai-gesprekken__datum">Stel Doortje een vraag</span>
            </button>
          </li>
        )}
        {zichtbaar.map((g) => {
          const actief = g.pap_conversation_id === activeId;
          const titel = g.titel || 'Nieuw gesprek';

          if (bewerkId === g.documentId) {
            return (
              <li key={g.documentId} className="ai-gesprekken__item ai-gesprekken__item--bewerk">
                <input
                  autoFocus
                  maxLength={120}
                  value={bewerkTitel}
                  onChange={(e) => setBewerkTitel(e.target.value)}
                  onBlur={opslaanTitel}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') opslaanTitel();
                    if (e.key === 'Escape') setBewerkId(null);
                  }}
                  aria-label="Naam van het gesprek"
                />
              </li>
            );
          }

          if (verwijderId === g.documentId) {
            return (
              <li key={g.documentId} className="ai-gesprekken__item ai-gesprekken__item--bevestig">
                <span>Gesprek verwijderen?</span>
                <div>
                  <button type="button" className="ai-gesprekken__gevaar" onClick={() => verwijder(g)}>
                    Verwijderen
                  </button>
                  <button type="button" onClick={() => setVerwijderId(null)}>
                    Annuleren
                  </button>
                </div>
              </li>
            );
          }

          return (
            <li key={g.documentId} className={`ai-gesprekken__item${actief ? ' ai-gesprekken__item--actief' : ''}`}>
              <button
                type="button"
                className="ai-gesprekken__kies"
                onClick={() => kies(g)}
                aria-current={actief ? 'true' : undefined}
                title={titel}
              >
                <span className="ai-gesprekken__titel">{titel}</span>
                <span className="ai-gesprekken__datum">
                  {bezigId === g.documentId ? 'Bezig…' : datumLabel(g.laatst_gebruikt_op || g.createdAt)}
                </span>
              </button>
              <div className="ai-gesprekken__acties">
                <button
                  type="button"
                  className="ai-gesprekken__actie"
                  onClick={() => startHernoemen(g)}
                  title="Hernoemen"
                  aria-label={`${titel} hernoemen`}
                >
                  <IconHernoemen />
                </button>
                <button
                  type="button"
                  className="ai-gesprekken__actie"
                  onClick={() => download(g)}
                  disabled={bezigId === g.documentId}
                  title="Downloaden als PDF"
                  aria-label={`${titel} downloaden als PDF`}
                >
                  <IconDownload />
                </button>
                <button
                  type="button"
                  className="ai-gesprekken__actie ai-gesprekken__actie--gevaar"
                  onClick={() => setVerwijderId(g.documentId)}
                  title="Verwijderen"
                  aria-label={`${titel} verwijderen`}
                >
                  <IconVerwijderen />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {isKaart && !alles && gesprekken.length > MOBIEL_ZICHTBAAR && (
        <button type="button" className="ai-gesprekken__meer" onClick={() => setAlleTonen((v) => !v)}>
          {alleTonen ? 'Minder tonen' : `Alle gesprekken tonen (${gesprekken.length})`}
        </button>
      )}
    </nav>
  );
}
